import { useCallback, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import * as Eden from "src/eden/content";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { Button } from "src/components/atoms/Button/Button";
import {
  useGetTeamEventOpponentSeasonStatsQuery,
  useGetTeamEventStatsByIDQuery,
  useGetTeamOpponentsByIDQuery,
  useGetTeamSprayChartByIDQuery,
} from "src/state/modules/api";
import { UseGameStateResult } from "src/components/util/useGameState";
import { BuiltGameState, SaberGameSnapshot } from "src/utils/sabertooth-client";
import OpponentGameModal from "./OpponentGameModal";
import SprayChartModal from "./SprayChartModal";
import DiscrepancyFlag from "./DiscrepancyFlag";
import { compareBoxScore } from "./box-score-diff";
import { HitterChip, HitterLegend } from "./HitterChip";
import { hitterNameKey } from "./hitter-rating";
import {
  PlayerMap,
  RosterPlayer,
  formatIP,
  playerFullName,
  playerNumber,
  playerPositions,
  stat,
  statContributors,
} from "./game-stream-helpers";
import Styles from "./BoxScore.module.scss";

type Ctx = { team: Team.Team; event: Event.Event; gameState: UseGameStateResult };

// Returns the season batting line for an opponent batter, or undefined when there's no scouting data
// for them (our team's side, or no correlation). The two box-score contexts wire it up differently:
// the main tab matches the opponent side by first-name+last-initial; the opponent modal matches its
// (progenitor) side by exact player id.
export type GetHitterSeason = (
  teamId: string,
  playerId: string,
  player?: RosterPlayer
) => Eden.OpponentSeasonBatter | undefined;

// Returns a batter's batted-ball spray data (undefined when we have none for them). On the main Box
// Score tab this is wired up for both teams — our /spray-charts payload is keyed by player id and
// includes opponent batters (their balls in play against us, under the same ids the box score uses).
export type GetSprayData = (playerId: string) => Eden.SprayChartData[] | undefined;

const BATTING_COLS = ["AB", "R", "H", "RBI", "BB", "SO"] as const;
const PITCHING_COLS = ["IP", "H", "R", "ER", "BB", "SO"] as const;

export default function BoxScorePage() {
  const { team, event, gameState } = useOutletContext<Ctx>();
  const { built, state, players, isLoading, isError } = gameState;
  const [showOpponent, setShowOpponent] = useState(false);

  // Drives the "View opponent box score" gating: the opponent has a real GameChanger team (its
  // progenitor) we can pull. Mirror the backend's resolution (GetOpponentEventGameStreamAsync) —
  // match the opponent by root_team_id first, then fall back to a progenitor-bearing record by name,
  // because the stored pregame opponent_id doesn't always equal the opponents-list root_team_id.
  const { data: opponents } = useGetTeamOpponentsByIDQuery(team.id);
  const opponentId = event.opponentTeam?.id;
  const opponentName = event.opponentTeam?.name;
  const byRoot = opponents?.find((o) => o.root_team_id === opponentId);
  const byName = opponentName
    ? opponents?.find((o) => o.name === opponentName && !!o.progenitor_team_id)
    : undefined;
  const hasProgenitor = !!byRoot?.progenitor_team_id || !!byName;

  // Opponent season-batting scouting cues. Only fetched once a final game is built and the opponent
  // has a resolvable progenitor (otherwise the endpoint just 404s). On our own scorebook the opponent
  // batters are stored with a last initial only, so we correlate them by first-name + last-initial.
  const { data: opponentSeason } = useGetTeamEventOpponentSeasonStatsQuery(
    { teamID: team.id, eventID: event.id },
    { skip: !hasProgenitor || !built }
  );

  const seasonByNameKey = useMemo(() => {
    const map = new Map<string, Eden.OpponentSeasonBatter>();
    for (const p of Object.values(opponentSeason?.players ?? {})) {
      map.set(hitterNameKey(p.first_name, p.last_name), p);
    }
    return map;
  }, [opponentSeason]);

  const getHitterSeason = useCallback<GetHitterSeason>(
    (panelTeamId, _playerId, player) => {
      if (panelTeamId === team.id || !player) return undefined; // cues are opponent-only
      return seasonByNameKey.get(hitterNameKey(player.first_name, player.last_name));
    },
    [seasonByNameKey, team.id]
  );

  // Per-batter spray charts. The team's season spray data is keyed by player id and already includes
  // opponent batters (their balls in play against us), so a single fetch covers both panels. Season-
  // wide, so it's the same payload across this team's events (RTK caches it).
  const { data: sprayCharts } = useGetTeamSprayChartByIDQuery(team.id, { skip: !built });

  const sprayByPlayer = useMemo(() => {
    const map = new Map<string, Eden.SprayChartData[]>();
    for (const ev of sprayCharts ?? []) {
      for (const [pid, hits] of Object.entries(ev.spray_chart_data?.offense ?? {})) {
        if (!hits?.length) continue;
        const existing = map.get(pid);
        if (existing) existing.push(...hits);
        else map.set(pid, [...hits]);
      }
    }
    return map;
  }, [sprayCharts]);

  const getSprayData = useCallback<GetSprayData>((playerId) => sprayByPlayer.get(playerId), [sprayByPlayer]);

  // GameChanger's official per-game book, to cross-check our sabertooth-derived box score against.
  // Only fetched for a built (final) game; for live games the numbers are still settling.
  const { data: gcStats } = useGetTeamEventStatsByIDQuery(
    { teamID: team.id, eventID: event.id },
    { skip: !built }
  );

  // Batting-line differences between our box score and GameChanger's official line, for our team.
  const diff = useMemo(
    () => compareBoxScore(state, team.id, gcStats?.eventStats, players),
    [state, team.id, gcStats?.eventStats, players]
  );

  if (isLoading && !state) return <PageLoader1 />;
  if (isError || !state || !built) {
    return <div className={Styles.empty}>Box score isn’t available for this game yet.</div>;
  }

  const oppName = event.opponentTeam?.name ?? "Opponent";
  const teamName = (id: string) => (id === team.id ? team.name : oppName);

  return (
    <>
      {opponentId && (
        <div className={Styles.opponentBar}>
          {hasProgenitor ? (
            <Button type="stroke" size="small" title={`View ${oppName} box score`} onClick={() => setShowOpponent(true)} />
          ) : (
            // Opponent never kept its own GameChanger book — disable with a hover explanation.
            <span title="This opponent didn’t keep score in GameChanger">
              <Button type="stroke" size="small" disabled title={`View ${oppName} box score`} />
            </span>
          )}
        </div>
      )}
      {diff.discrepancies.length > 0 && (
        <div className={Styles.flagWrap}>
          <DiscrepancyFlag discrepancies={diff.discrepancies} />
        </div>
      )}
      <BoxScoreView
        built={built}
        state={state}
        players={players}
        teamName={teamName}
        getHitterSeason={getHitterSeason}
        getSprayData={getSprayData}
        diffTeamId={team.id}
        diffByCell={diff.byCell}
      />
      {showOpponent && (
        <OpponentGameModal team={team} event={event} ourGameState={gameState} onClose={() => setShowOpponent(false)} />
      )}
    </>
  );
}

interface BoxScoreViewProps {
  built: BuiltGameState;
  state: SaberGameSnapshot;
  players: PlayerMap;
  // Maps a stream team id to its display name. Differs by context: our tabs map our id → our name,
  // the opponent modal maps the opponent's (progenitor) id → the opponent name (and vice versa).
  teamName: (id: string) => string;
  // Optional opponent scouting cues (season AVG/OPS/HR) shown under opponent batters' names.
  getHitterSeason?: GetHitterSeason;
  // Optional per-batter spray charts; when provided, a spray icon opens each batter's chart.
  getSprayData?: GetSprayData;
  // Box-score-vs-GameChanger differences. Only applied to the panel whose id == diffTeamId (our
  // team — GC's per-game stats are team-scoped), so the opponent panel is never highlighted.
  diffTeamId?: string;
  diffByCell?: Map<string, { box: number; gc: number }>;
}

// The presentational box score: two team panels (away on top, home below — matches the linescore in
// the event header) plus the legend. Reused by the event Box Score tab and the opponent modal.
export function BoxScoreView({
  built,
  state,
  players,
  teamName,
  getHitterSeason,
  getSprayData,
  diffTeamId,
  diffByCell,
}: BoxScoreViewProps) {
  const teamIds = [built.awayTeamId, built.homeTeamId];
  // The open batter's spray chart. Held here so the modal overlays the whole (still-mounted) box
  // score — closing it just clears this and you're back on the box score.
  const [sprayFor, setSprayFor] = useState<{ id: string; name: string } | null>(null);
  return (
    <div className={Styles.boxScore}>
      {teamIds.map((id) => (
        <TeamBoxScore
          key={id}
          state={state}
          players={players}
          teamId={id}
          name={teamName(id)}
          getHitterSeason={getHitterSeason}
          getSprayData={getSprayData}
          diffByCell={id === diffTeamId ? diffByCell : undefined}
          onOpenSpray={(playerId, playerName) => setSprayFor({ id: playerId, name: playerName })}
        />
      ))}
      {getHitterSeason && <HitterLegend />}
      <Legend />
      {sprayFor && (
        <SprayChartModal
          playerName={sprayFor.name}
          sprayData={getSprayData?.(sprayFor.id) ?? []}
          onClose={() => setSprayFor(null)}
        />
      )}
    </div>
  );
}

interface TeamProps {
  state: SaberGameSnapshot;
  players: PlayerMap;
  teamId: string;
  name: string;
  getHitterSeason?: GetHitterSeason;
  getSprayData?: GetSprayData;
  diffByCell?: Map<string, { box: number; gc: number }>;
  onOpenSpray?: (playerId: string, playerName: string) => void;
}

function TeamBoxScore({ state, players, teamId, name, getHitterSeason, getSprayData, diffByCell, onOpenSpray }: TeamProps) {
  const teamStats = state.teamStats[teamId];
  const situation = state.boxscore[teamId];
  if (!teamStats || !situation) return null;

  const batters = (situation.battingOrder ?? []).flatMap((slot) => slot);
  const pitcherIds: string[] = [];
  for (const ph of situation.pitcherHistory ?? []) {
    if (!pitcherIds.includes(ph.pitcherId)) pitcherIds.push(ph.pitcherId);
  }

  const offense = teamStats.stats?.offense;
  const defense = teamStats.stats?.defense;

  const notes = [
    { label: "2B", values: statContributors(teamStats, players, "offense", "2B") },
    { label: "3B", values: statContributors(teamStats, players, "offense", "3B") },
    { label: "HR", values: statContributors(teamStats, players, "offense", "HR") },
    { label: "TB", values: statContributors(teamStats, players, "offense", "TB") },
    { label: "HBP", values: statContributors(teamStats, players, "offense", "HBP") },
    { label: "SF", values: statContributors(teamStats, players, "offense", "SHF") },
    { label: "SB", values: statContributors(teamStats, players, "offense", "SB") },
    { label: "CS", values: statContributors(teamStats, players, "offense", "CS") },
    { label: "E", values: statContributors(teamStats, players, "defense", "E") },
  ].filter((n) => n.values.length > 0);

  const wp = statContributors(teamStats, players, "defense", "WP");

  return (
    <section className={Styles.teamPanel}>
      <h3 className={Styles.teamName}>{name}</h3>

      <table className={Styles.table}>
        <thead>
          <tr>
            <th className={Styles.nameCol}>Lineup</th>
            {BATTING_COLS.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {batters.map((id, idx) => {
            const line = teamStats.players[id]?.stats?.offense;
            const pos = playerPositions(situation, id);
            const season = getHitterSeason?.(teamId, id, players[id]);
            const hasSpray = (getSprayData?.(id)?.length ?? 0) > 0;
            const fullName = playerFullName(players[id]);
            return (
              <tr key={`${id}-${idx}`}>
                <td className={Styles.nameCol}>
                  <span className={Styles.batterName}>
                    {fullName} {playerNumber(players[id])}
                    {pos && <span className={Styles.pos}> ({pos})</span>}
                    {hasSpray && onOpenSpray && (
                      <SprayButton onClick={() => onOpenSpray(id, fullName)} label={`View ${fullName}’s spray chart`} />
                    )}
                  </span>
                  {season && <HitterChip season={season} />}
                </td>
                {BATTING_COLS.map((c) => {
                  const d = diffByCell?.get(`${id}:${c}`);
                  return (
                    <td key={c} className={d ? Styles.diffCell : undefined} title={d ? `GameChanger: ${d.gc}` : undefined}>
                      {stat(line, c)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr className={Styles.totalRow}>
            <td className={Styles.nameCol}>TEAM</td>
            {BATTING_COLS.map((c) => (
              <td key={c}>{stat(offense, c)}</td>
            ))}
          </tr>
        </tbody>
      </table>

      {notes.length > 0 && (
        <div className={Styles.notes}>
          {notes.map((n) => (
            <div key={n.label}>
              <span className={Styles.noteLabel}>{n.label}:</span> {n.values.join(", ")}
            </div>
          ))}
        </div>
      )}

      <table className={Styles.table}>
        <thead>
          <tr>
            <th className={Styles.nameCol}>Pitching</th>
            {PITCHING_COLS.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pitcherIds.map((id) => {
            const d = teamStats.players[id]?.stats?.defense;
            return (
              <tr key={id}>
                <td className={Styles.nameCol}>
                  {playerFullName(players[id])} {playerNumber(players[id])}
                </td>
                <td>{formatIP(stat(d, "outs"))}</td>
                {PITCHING_COLS.slice(1).map((c) => (
                  <td key={c}>{stat(d, c)}</td>
                ))}
              </tr>
            );
          })}
          <tr className={Styles.totalRow}>
            <td className={Styles.nameCol}>TEAM</td>
            <td>{formatIP(stat(defense, "outs"))}</td>
            {PITCHING_COLS.slice(1).map((c) => (
              <td key={c}>{stat(defense, c)}</td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className={Styles.notes}>
        {wp.length > 0 && (
          <div>
            <span className={Styles.noteLabel}>WP:</span> {wp.join(", ")}
          </div>
        )}
        <div>
          <span className={Styles.noteLabel}>Pitches-Strikes:</span>{" "}
          {pitcherIds
            .map((id) => {
              const d = teamStats.players[id]?.stats?.defense;
              const pitches = stat(d, "#P");
              return `${playerFullName(players[id])} ${pitches}-${Math.round(pitches * stat(d, "S%"))}`;
            })
            .join(", ")}
        </div>
        <div>
          <span className={Styles.noteLabel}>Batters Faced:</span>{" "}
          {pitcherIds
            .map((id) => `${playerFullName(players[id])} ${stat(teamStats.players[id]?.stats?.defense, "BF")}`)
            .join(", ")}
        </div>
      </div>
    </section>
  );
}

// Small spray-chart launcher shown inline next to a batter's name (a fan of field + two hit dots).
function SprayButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" className={Styles.sprayBtn} onClick={onClick} aria-label={label} title={label}>
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
        <path d="M12 20 L4 8 A11 11 0 0 1 20 8 Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="9.5" cy="10.5" r="1.3" fill="currentColor" />
        <circle cx="14.5" cy="8.5" r="1.3" fill="currentColor" />
      </svg>
    </button>
  );
}

// Ordered to fill the three legend columns top-to-bottom, like the GC web app.
const LEGEND: Array<[string, string]> = [
  ["2B", "Doubles"],
  ["3B", "Triples"],
  ["AB", "At bats"],
  ["BB", "Base on balls (walks)"],
  ["CS", "Caught stealing"],
  ["E", "Errors"],
  ["ER", "Earned runs allowed"],
  ["H", "Hits allowed"],
  ["HBP", "Hit batters"],
  ["HR", "Home runs"],
  ["IP", "Innings Pitched"],
  ["R", "Runs allowed"],
  ["RBI", "Runs batted in"],
  ["SB", "Stolen bases"],
  ["SF", "Sacrifice flies"],
  ["SO", "Strikeouts"],
  ["TB", "Total bases"],
  ["WP", "Wild pitches"],
];

function Legend() {
  return (
    <div className={Styles.legend}>
      {LEGEND.map(([abbr, description]) => (
        <span key={abbr}>
          <span className={Styles.legendAbbr}>{abbr}</span> - {description}
        </span>
      ))}
    </div>
  );
}
